import React from 'react';
import { Table } from 'antd';
import { Icon } from '@common/components';
import { formatAllowListsToAllowedUnits } from '@scheduling/components';
import {
  AllowListItemType,
  OrganizationUnitType,
  RoleType,
} from '@scheduling/graphql/queries/worker-detail';
import { moveArrayItem } from 'works/legacy/services/array-service/array-service';
import { WorkerDetailQueryCgResponse } from '@common/graphql/schema';

type Props = {
  allowLists?: WorkerDetailQueryCgResponse['worker']['allowLists'];
  allowedUnits?: WorkerDetailQueryCgResponse['worker']['allowedUnits'];
  homeUnitId?: string;
};

type DataSourceItemType = OrganizationUnitType & {
  isHomeUnit: boolean;
  roles: string[];
};

export function WorkerDetailUnitsTable({
  allowLists,
  allowedUnits,
  homeUnitId,
}: Props) {
  const allowListPerUnit = allowLists
    ? formatAllowListsToAllowedUnits(homeUnitId, allowLists)
    : [];

  const allRoles = getAllRolesAvailables(allowLists || []);

  const rawDataSource = buildDataSource(allowListPerUnit, allowedUnits || []);

  const dataSource = bringHomeUnitToTheTop(
    rawDataSource as DataSourceItemType[]
  );

  const columns = [
    {
      title: 'Unit / Cost Center',
      key: 'unit',
      width: '50%',
      render: (item: DataSourceItemType) => (
        <>
          <div>
            {item?.name}
            {item?.isHomeUnit && (
              <span style={{ marginLeft: 8 }}>
                <Icon icon="houseChimneyMedical" kind="regular" />
              </span>
            )}
          </div>
          <span className="color-midnight-lighter">{item?.costCenter}</span>
        </>
      ),
    },
    {
      title: 'Roles',
      key: 'roles',
      width: '50%',
      dataIndex: 'roles',
      render: (roles: string[]) => {
        const rolesAbbreviations = roles?.map(
          (roleId: string) => allRoles[roleId]?.abbreviation
        );

        return rolesAbbreviations?.join(', ');
      },
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      pagination={{ showSizeChanger: true }}
    />
  );
}

function getAllRolesAvailables(allowLists: AllowListItemType[] | undefined) {
  return (
    allowLists?.reduce(
      (uniqueRoles, item) => {
        uniqueRoles[item.role.id] = item.role;
        return uniqueRoles;
      },
      {} as Record<string, RoleType>
    ) || {}
  );
}

type allowListPerUnitType = {
  isHomeUnit: boolean;
  roles: string[];
  unit?: string;
};

function buildDataSource(
  allowListPerUnit: allowListPerUnitType[],
  allowedUnits?: WorkerDetailQueryCgResponse['worker']['allowedUnits']
) {
  return (
    allowedUnits?.map((unit) => {
      const allowListItem =
        allowListPerUnit?.find((item) => item.unit === unit.id) ||
        ({} as allowListPerUnitType);

      delete allowListItem.unit;

      return {
        ...unit,
        ...allowListItem,
      };
    }) || ([] as DataSourceItemType[])
  );
}

function bringHomeUnitToTheTop(rawDataSource: DataSourceItemType[]) {
  const homeUnitItemIndex = rawDataSource.findIndex((item) => item.isHomeUnit);

  if (homeUnitItemIndex === -1) return rawDataSource;

  return moveArrayItem<DataSourceItemType>(rawDataSource, homeUnitItemIndex, 0);
}
